import { useEffect, useRef, useState } from "react";
import { Bell, Trophy, Share2 } from "lucide-react";
import api, { TIER_META, MILESTONES, OCCASION_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";

const RANK_ORDER = ["rising", "top", "legend"];

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [earnings, setEarnings] = useState(null);
  const { share, busyId, shareRef, shareItem } = useRoastShare();
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => { setNotes(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      api.post("/notifications/read", {}).catch(() => load());
    }
  };

  const shareClimb = async (id) => {
    let e = earnings;
    if (!e) { try { e = (await api.get("/roaster/earnings")).data; setEarnings(e); } catch { return; } }
    const wins = e.accepted_count;
    const nextKey = RANK_ORDER.find((k) => MILESTONES[k].threshold > wins) || null;
    const curKey = [...RANK_ORDER].reverse().find((k) => wins >= MILESTONES[k].threshold) || null;
    if (!nextKey) {
      share({ kind: "milestone", flairKey: "legend", wins, roaster_name: user?.name, roaster_avatar: user?.avatar }, id);
      return;
    }
    const floor = curKey ? MILESTONES[curKey].threshold : 0;
    const nt = MILESTONES[nextKey].threshold;
    share({
      kind: "progress", rankLabel: curKey ? MILESTONES[curKey].label : null,
      nextLabel: MILESTONES[nextKey].label, nextKey, wins, nextThreshold: nt, floor,
      pct: Math.min(100, Math.round(((wins - floor) / (nt - floor)) * 100)),
      roaster_name: user?.name, roaster_avatar: user?.avatar,
    }, id);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle} aria-label="Notifications"
        className="relative text-zinc-400 hover:text-brand transition-colors p-1">
        <Bell size={19} />
        {unread > 0 && (
          <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-brand text-black text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto z-50 rounded-sm border border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl shadow-black/50">
            <div className="px-4 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-widest text-zinc-400">Notifications</div>
            {notes.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">No wins yet. Go roast something 🔥</div>
            ) : (
              notes.map((n) => {
                const meta = TIER_META[n.tier] || TIER_META.savage;
                if (n.type === "verdict_ready") {
                  return (
                    <a key={n.id} data-testid={`notif-${n.id}`} href="/app/tasks"
                      className={`block px-4 py-3 border-b border-white/5 hover:bg-white/5 ${n.read ? "" : "bg-brand/5"}`}>
                      <div className="flex items-start gap-2.5">
                        <Trophy size={16} className="mt-0.5 shrink-0 text-brand" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 leading-snug">
                            Your <span className="font-bold text-brand">{n.category_name}</span> verdict is in
                            {n.headline && <span className="font-bold whitespace-nowrap"> · {n.headline}</span>}
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)} · tap to reveal</div>
                        </div>
                      </div>
                    </a>
                  );
                }
                if (n.type === "gift_reveal") {
                  const occ = OCCASION_META[n.occasion] || OCCASION_META.just_because;
                  return (
                    <a key={n.id} data-testid={`notif-${n.id}`} href={`/reveal/${n.reveal_token}`} target="_blank" rel="noreferrer"
                      className={`block px-4 py-3 border-b border-white/5 hover:bg-white/5 ${n.read ? "" : "bg-brand/5"}`}>
                      <div className="flex items-start gap-2.5">
                        <span className="text-lg leading-none mt-0.5">{occ.emoji}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 leading-snug">Someone roasted you for your <span className="font-bold text-brand">{occ.label.toLowerCase()}</span> — tap to see 👀</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
                        </div>
                      </div>
                    </a>
                  );
                }
                if (n.type === "tip") {
                  return (
                    <div key={n.id} data-testid={`notif-${n.id}`} className={`px-4 py-3 border-b border-white/5 ${n.read ? "" : "bg-brand/5"}`}>
                      <div className="flex items-start gap-2.5">
                        <span className="text-lg leading-none mt-0.5">💸</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 leading-snug">{n.from_name || "Someone"} tipped you <span className="font-bold text-[#d4af37]">+{n.symbol}{Number(n.amount).toFixed(2)}</span></div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={n.id} data-testid={`notif-${n.id}`} className={`px-4 py-3 border-b border-white/5 ${n.read ? "" : "bg-brand/5"}`}>
                    <div className="flex items-start gap-2.5">
                      <Trophy size={16} className="mt-0.5 shrink-0" style={{ color: meta.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-100 leading-snug">
                          Your <span className="font-bold" style={{ color: meta.color }}>{meta.label}</span> roast was picked
                          <span className="text-tier-mild font-bold whitespace-nowrap"> · +{n.symbol}{Number(n.amount).toFixed(2)}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
                        <button data-testid={`notif-share-${n.id}`} onClick={() => shareClimb(n.id)} disabled={busyId === n.id}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-xs px-3 py-1.5 transition-colors">
                          <Share2 size={12} /> {busyId === n.id ? "Preparing…" : "Share your climb 🔥"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
};

export default NotificationBell;
